import React, { Fragment, useCallback, useContext, useMemo } from 'react'
import styled, { withTheme } from 'styled-components'
import Spinner from 'react-loader-spinner'
import { IconArrowsExpandSide, IconClock } from '@loadsmart/icons'
import formatCurrency from 'utils/formatCurrency'
import { formatAddress } from 'utils/address'
import LaneAwardForm from './LaneAwardForm'
import {
  Table as BaseTable,
  THead as BaseHeader,
  Row as BaseRow,
  Column,
} from 'screens/Shipper/components/Table'
import { Theme } from 'styles/theme'
import Button from 'components/Button'
import {
  useSortBy,
  useTable,
  UseTableOptions,
  Row as RowType,
  Column as ColumnType,
} from 'react-table'
import { Checkbox as BaseCheckbox } from 'components/Input'
import { RFPDetailActionsContext } from '.'
import {
  calculateSuggestedVolume,
  getAverageRatePrice,
  getAwardedVolume,
  getHighestBid,
  getLowestBid,
} from './utils'
import Alert, { Wrapper as AlertWrapper } from 'components/Alert'
import { numberFormatter } from 'utils/numbers'

const Capacity = styled.span<{ highlight: boolean }>`
  ${({ highlight, theme }) =>
    highlight &&
    `
    color: ${theme.colors.textStatusYellow};
  `}
`
const Table = styled(BaseTable)`
  border-spacing: 0;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
`
const THead = styled(BaseHeader)`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;

  .arrow {
    color: ${({ theme }) => theme.colors.borderSecondary};
    font-size: 10px;
    margin-right: 8px;
  }
  th:first-child {
    padding-left: 20px;
  }
  th:last-child {
    padding-right: 20px;
  }
`
const Row = styled(BaseRow)`
  :hover {
    background: ${({ selected, selectedColor }) =>
      selected ? selectedColor : 'none'};
  }

  > th,
  > td {
    display: table-cell;
    padding: 10px 5px;

    &:first-child {
      padding-left: 20px;
    }
    &:last-child {
      padding-right: 20px;
    }
  }
`
const Arrow = styled.div<{ open: boolean }>`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 4px 0px 0px 4px;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: absolute;
  padding: 20px 5px;
  cursor: pointer;
  top: 98px;
  right: ${({ open }) => (open ? '540px' : '0')};

  ${({ open }) =>
    !open &&
    `
    svg {
      transform: rotate(180deg);
    }
  `}
`
const iconSize = 14
const ProposalsContainer = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  font-weight: 600;
  height: calc(100vh - 20px);
  justify-content: space-between;
  padding: 20px 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 540px;
  font-size: 14px;
`
const ProposalsInnerContainer = styled.div`
  overflow-y: auto;
`
const LaneTitle = styled.h1`
  color: ${({ theme }) => theme.colors.white};
  font-weight: bold;
  font-size: 20px;
  line-height: 26px;
  text-align: center;
  margin: 0;
  padding: 0 20px 20px 20px;
`
const NavigationContainer = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.textSecondary};
  display: grid;
  grid-gap: 29px 34px;
  grid-template-columns: 1fr 1fr;
  padding: 16px 20px;

  ${AlertWrapper} {
    grid-column-start: 1;
    grid-column-end: 3;
    margin: 0;
  }

  ${Button} {
    margin: 0;
    height: 42px;
  }
`
const Checkbox = styled(BaseCheckbox)`
  background-color: ${({ theme }) => theme.colors.white};
`
const SpinnerWrapper = styled.div`
  text-align: center;
`
const CheckboxCell = styled.div`
  align-items: center;
  display: inline-flex;

  ${Checkbox}, ${SpinnerWrapper} {
    margin-right: 8px;
  }
`
const RatePriceCell = styled.span<{
  isHighestBid: boolean
  isLowestBid: boolean
}>`
  color: ${({ isLowestBid, isHighestBid, theme }) => {
    if (isHighestBid) {
      return theme.colors.danger
    } else if (isLowestBid) {
      return theme.colors.main
    }
    return theme.colors.white
  }};
  font-weight: 700;
`
const Title = styled.h3`
  color: ${({ theme }) => theme.colors.white};
  font-size: 20px;
  font-weight: 700;
  margin: 30px 0 15px 0;
  padding: 0 20px;
`
const EmptyRow = styled(Row)`
  text-transform: none;

  ${Column} {
    padding-top: 92px;

    h3 {
      font-size: 18px;
      font-weight: 500;
      margin: 20px 0 10px;
    }
    p {
      color: ${({ theme }) => theme.colors.borderLight};
      font-size: 14px;
      margin: 0;
    }
  }
`
const Heading = styled.div`
  background-color: ${({ theme }) => theme.colors.borderSecondary};
  border-radius: ${({ theme }) => theme.borderRadiuses.normal};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  margin: 4px 20px;
  padding: 12px;
`
const CarrierCell = ({
  proposals,
  lane,
  proposal,
  carrier,
}: {
  proposals: LaneProposal[]
  lane?: Lane
  proposal: LaneProposal
  carrier?: Carrier
}) => {
  const { partialUpdateLaneProposal } = useContext(RFPDetailActionsContext)
  const onToggle = useCallback(() => {
    let suggestedVolume = 0
    if (lane) {
      suggestedVolume = calculateSuggestedVolume({
        lane,
        proposal,
        proposals,
      })
    }

    partialUpdateLaneProposal(
      proposal.id,
      proposal.awarded_type
        ? {
            awarded_type: null,
            awarded_volume: 0,
          }
        : {
            awarded_type: suggestedVolume > 0 ? 'primary' : 'backup',
            awarded_volume: suggestedVolume,
          }
    )
  }, [proposal, partialUpdateLaneProposal, lane, proposals])
  return (
    <CheckboxCell>
      {proposal.meta?.isUpdating ? (
        <SpinnerWrapper>
          <Spinner type="TailSpin" width={24} height={24} />
        </SpinnerWrapper>
      ) : (
        <Checkbox inline onToggle={onToggle} value={!!proposal.awarded_type} />
      )}
      <span>{carrier ? carrier.name || carrier.email : '-'}</span>
    </CheckboxCell>
  )
}
function LaneProposal({
  lane,
  proposals = [],
  isOpen,
  isLoading,
  onToggleVisible,
  theme,
  selectLane,
  nextLane,
  previousLane,
}: {
  lane: Lane | undefined
  proposals?: LaneProposal[]
  isOpen: boolean
  isLoading: boolean
  onToggleVisible: () => void
  theme: Theme
  selectLane: (lane: Lane) => void
  nextLane: Lane | undefined
  previousLane: Lane | undefined
}) {
  type LaneColumnType = ColumnType<LaneProposal> & {
    align?: 'left' | 'right' | 'center'
  }
  const { averageRatePrice, awardedVolume, hightestBid, lowestBid } = useMemo(
    () => ({
      averageRatePrice: getAverageRatePrice(proposals),
      awardedVolume: getAwardedVolume(proposals),
      hightestBid: getHighestBid(proposals),
      lowestBid: getLowestBid(proposals),
    }),
    [proposals]
  )
  const columns = useMemo(
    (): LaneColumnType[] => [
      {
        accessor: 'carrier',
        align: 'left',
        Cell: function Cell({
          row,
          value,
        }: {
          row: RowType<LaneProposal>
          value?: Carrier
        }) {
          return (
            <CarrierCell
              carrier={value}
              lane={lane}
              proposal={row.original}
              proposals={proposals}
            />
          )
        },
        Header: 'Carrier',
      },
      {
        accessor: 'capacity',
        Cell: function Cell({ value }: { value: number }) {
          return (
            <Capacity highlight={!!(lane && lane.volume > value)}>
              {value}
            </Capacity>
          )
        },
        Header: 'Vol',
      },
      {
        align: 'right',
        accessor: 'rate_price',
        Cell: function Cell({ value }: { value: number }) {
          return (
            <RatePriceCell
              isHighestBid={value >= hightestBid}
              isLowestBid={value <= lowestBid}
            >
              {formatCurrency(value)}
            </RatePriceCell>
          )
        },
        Header: 'Bid',
      },
    ],
    [hightestBid, lane, lowestBid, proposals]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable<LaneProposal>(
    {
      autoResetSortBy: false,
      columns,
      data: proposals,
      disableSortRemove: true,
      initialState: { sortBy: [{ id: 'rate_price' }] },
    } as UseTableOptions<LaneProposal>,
    useSortBy
  )

  return (
    <div>
      <Arrow
        onClick={onToggleVisible}
        open={isOpen}
        data-testid="laneProposalsExpand"
      >
        <IconArrowsExpandSide
          width={iconSize}
          height={iconSize}
          open={isOpen}
        />
      </Arrow>
      {isOpen && (
        <ProposalsContainer data-testid="proposalsContainer">
          <ProposalsInnerContainer>
            <LaneTitle>
              {formatAddress(
                lane?.origin_city,
                lane?.origin_state,
                lane?.origin_zip
              )}
              &nbsp;-&nbsp;
              {formatAddress(lane?.dest_city, lane?.dest_state, lane?.dest_zip)}
            </LaneTitle>
            {averageRatePrice > 0 && !isLoading && (
              <Heading aria-label="Average Bid Price">
                <span>Your average </span>
                <b>{formatCurrency(averageRatePrice)}</b>
              </Heading>
            )}
            <Title>Carrier Bids</Title>
            {proposals && (
              <Table {...getTableProps()}>
                <THead>
                  {headerGroups.map((headerGroup) => (
                    <Row
                      {...headerGroup.getHeaderGroupProps()}
                      key={headerGroup.getHeaderGroupProps().key}
                    >
                      {headerGroup.headers.map((column: any) => (
                        <th
                          {...column.getHeaderProps({
                            ...column.getSortByToggleProps(),
                            align: column.align || 'center',
                          })}
                          key={column.getHeaderProps().key}
                        >
                          {column.isSorted && (
                            <span className="arrow">
                              {column.isSortedDesc ? '▼' : '▲'}
                            </span>
                          )}
                          {column.render('Header')}
                        </th>
                      ))}
                    </Row>
                  ))}
                </THead>
                <tbody {...getTableBodyProps()}>
                  {isLoading ? (
                    <Row>
                      <Column align="center" colSpan={10}>
                        <Spinner type="ThreeDots" width={18} height={18} />
                      </Column>
                    </Row>
                  ) : rows?.length ? (
                    rows.map((row) => {
                      const awarded = !!row.original.awarded_type

                      prepareRow(row)

                      return (
                        <Fragment key={row.id}>
                          <Row
                            selected={awarded}
                            selectedColor={theme.colors.textSecondary}
                          >
                            {row.cells.map((cell: any) => (
                              <Column
                                {...cell.getCellProps({
                                  align: cell.column.align || 'center',
                                  style: cell.column.style,
                                })}
                                key={cell.getCellProps().key}
                              >
                                {cell.render('Cell')}
                              </Column>
                            ))}
                          </Row>
                          {awarded && (
                            <Row
                              selected={awarded}
                              selectedColor={theme.colors.textSecondary}
                            >
                              <Column colSpan={columns.length}>
                                <LaneAwardForm proposal={row.original} />
                              </Column>
                            </Row>
                          )}
                        </Fragment>
                      )
                    })
                  ) : (
                    <EmptyRow>
                      <Column align="center" colSpan={columns.length}>
                        <IconClock height={48} width={48} />
                        <h3>There are no responses yet.</h3>
                        <p>Please check back later.</p>
                      </Column>
                    </EmptyRow>
                  )}
                </tbody>
              </Table>
            )}
          </ProposalsInnerContainer>
          <NavigationContainer>
            {!isLoading && lane && lane.volume < awardedVolume && (
              <Alert type="info">
                <b>
                  Awarded volume ({numberFormatter(awardedVolume)}) is greater
                  than lane volume ({numberFormatter(lane.volume)}).
                </b>
              </Alert>
            )}
            <Button
              disabled={!previousLane}
              onClick={() => previousLane && selectLane(previousLane)}
              variant="dark"
            >
              PREVIOUS LANE
            </Button>
            <Button
              disabled={!nextLane}
              onClick={() => nextLane && selectLane(nextLane)}
              variant="dark"
            >
              NEXT LANE
            </Button>
          </NavigationContainer>
        </ProposalsContainer>
      )}
    </div>
  )
}

export default withTheme(LaneProposal)
